import { useState } from "react";
import toast from "react-hot-toast";
import moment from "moment";
import { FiAlertTriangle, FiRefreshCw } from "react-icons/fi";
import { MdOutlineEdit, MdSwapHoriz } from "react-icons/md";

import VendorAvatar from "./VendorAvatar";
import VendorPicker from "./VendorPicker";
import { useUpdatePaidVendor } from "../../hooks/paid-vendors";
import { useSignedUrlMap } from "../../hooks/useSignedUrlMap";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Side panel for an existing entry.
 *
 * Two distinct jobs, which the layout keeps apart:
 *  - re-point the entry at a different vendor (the wrong one was picked), and
 *  - correct the contact details recorded on the entry itself. Those are the
 *    details shown once the vendor account is gone, so they mostly matter for
 *    orphaned entries — the copy says as much rather than leaving the admin to
 *    wonder why an edit doesn't change the row.
 */
const EditPaidVendor = ({ paidVendor, onSaved, onCancel }) => {
  const [name, setName] = useState(paidVendor.recordedName || paidVendor.name || "");
  const [email, setEmail] = useState(paidVendor.recordedEmail || paidVendor.email || "");
  const [replacement, setReplacement] = useState(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [errors, setErrors] = useState({});

  const { mutate: updatePaidVendor, isPending } = useUpdatePaidVendor();
  const avatars = useSignedUrlMap([paidVendor.profilePic]);

  // Only meaningful while the vendor exists — otherwise there is nothing live
  // to compare the recorded details against.
  const isOutOfSync =
    paidVendor.vendorExists &&
    (paidVendor.name !== name || paidVendor.email !== email);

  const validate = () => {
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = "Name is required";
    if (!EMAIL_PATTERN.test(email.trim())) nextErrors.email = "Enter a valid email address";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const save = () => {
    if (!replacement && !validate()) return;

    // A swap re-snapshots server-side from the new vendor, so sending the old
    // recorded details along would immediately overwrite them.
    const body = replacement
      ? { vendorId: replacement._id }
      : { name: name.trim(), email: email.trim() };

    updatePaidVendor(
      { vendorId: paidVendor.vendorId, body },
      {
        onSuccess: (data) => {
          toast.success(data?.message || "Paid vendor updated");
          onSaved();
        },
        onError: (error) => toast.error(error.message),
      }
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-1 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-primary8 text-primary flex items-center justify-center">
            <MdOutlineEdit className="text-lg" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Edit Paid Vendor</h2>
            <p className="text-xs text-gray-500">
              Added {moment(paidVendor.createdAt).format("DD MMM YYYY")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4 px-1 space-y-5">
        {!paidVendor.vendorExists && (
          <div className="flex gap-2.5 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <FiAlertTriangle className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">
              The vendor account linked to this entry no longer exists. Point it at an
              active vendor, or remove the entry.
            </p>
          </div>
        )}

        {/* Linked vendor */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">Linked vendor</label>
            {!isSwapping && (
              <button
                type="button"
                onClick={() => setIsSwapping(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <MdSwapHoriz className="text-base" /> Change vendor
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 border border-gray-200 rounded-xl p-3 bg-gray-50">
            <VendorAvatar
              src={avatars.get(paidVendor.profilePic)}
              name={paidVendor.name}
              email={paidVendor.email}
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">
                {paidVendor.name || "Unnamed vendor"}
              </p>
              <p className="text-xs text-gray-500 truncate">{paidVendor.email}</p>
              {paidVendor.organization && (
                <p className="text-[11px] text-gray-400 truncate">{paidVendor.organization}</p>
              )}
            </div>
          </div>

          {isSwapping && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-gray-500">
                Pick the vendor this entry should point at. Their name and email replace
                the recorded details below.
              </p>
              <VendorPicker
                multiple={false}
                selected={replacement ? [replacement] : []}
                onChange={([vendor]) => setReplacement(vendor)}
              />
              <button
                type="button"
                onClick={() => {
                  setIsSwapping(false);
                  setReplacement(null);
                }}
                className="text-xs text-gray-500 hover:underline"
              >
                Keep the current vendor
              </button>
            </div>
          )}
        </section>

        {/* Recorded details */}
        <section className={replacement ? "opacity-50 pointer-events-none" : ""}>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-gray-700">Recorded details</label>
            {isOutOfSync && (
              <button
                type="button"
                onClick={() => {
                  setName(paidVendor.name || "");
                  setEmail(paidVendor.email || "");
                  setErrors({});
                }}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <FiRefreshCw className="text-[13px]" /> Use vendor's current details
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Kept on the paid-vendor entry and shown if the vendor account is ever
            deleted. While the vendor is active, their current name and email are what
            the list displays.
          </p>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Name <span className="text-primary">*</span>
              </label>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                className={`w-full border rounded-lg py-2.5 px-3 text-sm focus:outline-none focus:ring-1 ${
                  errors.name
                    ? "border-red-400 focus:ring-red-400"
                    : "border-gray-300 focus:ring-primary focus:border-primary"
                }`}
                placeholder="Vendor name"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Email <span className="text-primary">*</span>
              </label>
              <input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={`w-full border rounded-lg py-2.5 px-3 text-sm focus:outline-none focus:ring-1 ${
                  errors.email
                    ? "border-red-400 focus:ring-red-400"
                    : "border-gray-300 focus:ring-primary focus:border-primary"
                }`}
                placeholder="vendor@company.com"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>
        </section>
      </div>

      <div className="flex justify-end gap-3 px-1 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={isPending}
          className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:brightness-95 disabled:opacity-50"
        >
          {isPending ? "Saving..." : replacement ? "Change vendor" : "Save changes"}
        </button>
      </div>
    </div>
  );
};

export default EditPaidVendor;
